import type { Filter } from 'components/filters/useStreamFilters'
import { SATOSHIS } from 'lib/bsv-util'
import { ordinalsTopWindowParam } from 'components/panels/streamOrderMapping'

// Stepped stops for the ordinals public wall's quality dial. Mirrored by
// Ordinals::StreamThreshold::STOPS on the backend — the server snaps anything
// else to its default, so only these values are meaningful to send.
export const ORDINALS_MIN_SATS_STOPS = [0, 10_000, 100_000, 1_000_000, 10_000_000, 100_000_000]

export const DEFAULT_ORDINALS_MIN_SATS = 10_000

export const ORDINALS_MIN_SATS_STORAGE_KEY = 'ordinalsMinSats'

export function normalizeOrdinalsMinSats(value: unknown): number | null {
    const num =
        typeof value === 'string' && /^\d+$/.test(value)
            ? Number(value)
            : typeof value === 'number'
            ? value
            : NaN
    return ORDINALS_MIN_SATS_STOPS.includes(num) ? num : null
}

// The viewer's effective dial stop: explicit panel state first, then the saved
// space option, then the logged-out localStorage echo, then the default.
export function resolveOrdinalsMinSats(filter?: Filter | null): number {
    const fromFilter = normalizeOrdinalsMinSats(filter?.min_sats)
    if (fromFilter !== null) return fromFilter

    const browserWindow = typeof window === 'undefined' ? undefined : window
    const fromSpace = normalizeOrdinalsMinSats(
        browserWindow?.gon?.currentUser?.space_options?.ordinalsMinSats,
    )
    if (fromSpace !== null) return fromSpace

    // Anonymous preferences must not leak into a signed-in account. A logged-in
    // viewer with no saved stop gets the server-configured product default.
    if (browserWindow?.gon?.currentUser) {
        return (
            normalizeOrdinalsMinSats(browserWindow.gon?.ordinalsStreamDefaultMinSats) ??
            DEFAULT_ORDINALS_MIN_SATS
        )
    }

    try {
        const fromLocal = normalizeOrdinalsMinSats(
            browserWindow?.localStorage?.getItem(ORDINALS_MIN_SATS_STORAGE_KEY),
        )
        if (fromLocal !== null) return fromLocal
    } catch {
        // Storage can be unavailable (private mode); fall through to default.
    }

    return (
        normalizeOrdinalsMinSats(browserWindow?.gon?.ordinalsStreamDefaultMinSats) ??
        DEFAULT_ORDINALS_MIN_SATS
    )
}

function formatOrdinalsMinUsd(value: number, exchangeRate?: number | null): string | null {
    if (typeof exchangeRate !== 'number' || !Number.isFinite(exchangeRate) || exchangeRate <= 0) {
        return null
    }

    const amount = (value / SATOSHIS) * exchangeRate
    if (!Number.isFinite(amount) || amount <= 0) return null

    // Keep roughly three significant decimal digits below $1, while retaining
    // cents above it. This preserves useful precision for the smallest stop
    // without introducing scientific notation into the compact pill.
    const magnitude = Math.floor(Math.log10(amount))
    const fractionDigits = Math.min(6, Math.max(2, 2 - magnitude))
    const formatted = amount
        .toFixed(fractionDigits)
        .replace(/(\.\d*?[1-9])0+$/, '$1')
        .replace(/\.0+$/, '')

    return `$${formatted}`
}

// Compact label for the pill face: 'All', '10K+', '$0.001+'.
export function formatOrdinalsMinSats(
    value: number,
    useUsd = false,
    exchangeRate?: number | null,
): string {
    if (!value) return 'All'
    const usdLabel = useUsd ? formatOrdinalsMinUsd(value, exchangeRate) : null
    if (usdLabel) return `${usdLabel}+`
    if (value >= 1_000_000) return `${value / 1_000_000}M+`
    if (value >= 1_000) return `${value / 1_000}K+`
    return `${value}+`
}

// The stop written out for menu rows, tooltips and empty-state prose. A USD
// label carries its own symbol, so only sats takes a spelled-out unit.
export function formatOrdinalsMinSatsAmount(
    value: number,
    useUsd = false,
    { compact = false, exchangeRate }: { compact?: boolean; exchangeRate?: number | null } = {},
): string {
    const usdLabel = useUsd ? formatOrdinalsMinUsd(value, exchangeRate) : null
    if (usdLabel) return usdLabel
    const amount = compact ? formatOrdinalsMinSats(value).replace('+', '') : value.toLocaleString()
    return `${amount} sats`
}

// The stream's effective query filter: the dial stop is resolved into the
// filter for the global stream so the query key (and cache identity) always
// carries the bar actually applied server-side. The Market lens shares the
// wall's dial, so the same saved stop travels with the viewer between tabs.
export function ordinalsStreamQueryFilter(filter: Filter): Filter {
    const isGlobalOrdinals =
        !!filter?.ordinals && !filter?.user_ordinals && filter?.ordinals_scope !== 'mine'
    if (!isGlobalOrdinals) return filter

    // Following is temporarily hidden. Strip stale saved/history state so all
    // global Ordinals cache keys resolve to the one public stream.
    const globalFilter = { ...filter }
    delete globalFilter.ordinals_scope
    // A window only rides the plain Top order (as the server applies it), so a
    // stale one must not fork the key of an order that ignores it.
    if (!ordinalsTopWindowParam(globalFilter.order, globalFilter.top_window)) {
        delete globalFilter.top_window
    }
    return {
        ...globalFilter,
        min_sats: resolveOrdinalsMinSats(globalFilter),
    }
}
